import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { LocationOn, Search as SearchIcon, NavigateNext, Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { GET_VOLUNTEER_DATABASE_TASKS } from './volunteer-database-model.gql';
import { GetVolunteerDatabaseTasks } from './__generated__/GetVolunteerDatabaseTasks';
import { LocationSearchMap } from './LocationSearchMap';
import { AddressSearchField } from './AddressSearchField';

interface LocationSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (params: LocationSearchParams) => void;
}

interface LocationSearchParams {
  lat: number;
  long: number;
  range: number;
  sort: 'distance' | 'distance_desc' | 'alphabetical';
  addressTaskFieldId: string;
}

interface AddressTaskField {
  taskFieldId: string;
  name: string;
  description?: string;
}

const useStyles = makeStyles(() =>
  createStyles({
    dialogTitle: {
      paddingBottom: '8px',
    },
    stepIndicator: {
      fontSize: '12px',
      color: '#6b7280',
      marginBottom: '4px',
      textTransform: 'uppercase',
      fontWeight: 500,
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: 600,
      marginBottom: '16px',
    },
    description: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '24px',
    },
    dialogContent: {
      paddingTop: '24px',
      paddingBottom: '4em',
    },
    mapContainer: {
      width: '100%',
      height: '300px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    filterBox: {
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '16px',
      backgroundColor: '#f9fafb',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    dialogActions: {
      padding: '16px 24px',
      borderTop: '1px solid #e5e7eb',
    },
  })
);

export const LocationSearchDialog = (props: LocationSearchDialogProps) => {
  const { open, onClose, onSearch } = props;
  const { isMobile } = useCampfireTheme();
  const classes = useStyles();

  // Step management
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  // Step 1: Address Task Field selection
  const [selectedTaskFieldId, setSelectedTaskFieldId] = useState<string>('');
  const [addressTaskFields, setAddressTaskFields] = useState<AddressTaskField[]>([]);

  // Step 2: Location and filters
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; long: number; address: string } | null>(null);
  const [searchAddress, setSearchAddress] = useState<string>('');
  const [range, setRange] = useState<number>(10);
  const [sortBy, setSortBy] = useState<'distance' | 'distance_desc' | 'alphabetical'>('distance');

  // Query for task fields - only fetch when dialog is open
  const { data: tasksData } = useCampfireQuery<GetVolunteerDatabaseTasks, {}>(GET_VOLUNTEER_DATABASE_TASKS, {
    options: {
      skip: !open,
    },
  });

  // Extract address task fields from the tasks data
  useEffect(() => {
    if (tasksData?.vm.tasks) {
      const addressFields: AddressTaskField[] = [];

      tasksData.vm.tasks.forEach((task) => {
        task.taskItems.forEach((item) => {
          if (
            item.__typename === 'VOLUNTEER_TaskItemFieldType' &&
            item.field &&
            item.field.__typename === 'VOLUNTEER_AddressTaskFieldType'
          ) {
            addressFields.push({
              taskFieldId: item.field.taskFieldId,
              name: item.field.name,
              description: item.field.description || undefined,
            });
          }
        });
      });

      setAddressTaskFields(addressFields);
    }
  }, [tasksData]);

  const handleNext = () => {
    if (currentStep === 1 && selectedTaskFieldId) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleLocationSelect = (location: { lat: number; long: number; address: string }) => {
    setSelectedLocation(location);
    setSearchAddress(location.address);
  };

  const handleMarkerDrag = (lat: number, long: number) => {
    setSelectedLocation((prev) => (prev ? { ...prev, lat, long } : null));
  };

  const handleSearch = () => {
    if (selectedLocation && selectedTaskFieldId) {
      onSearch({
        lat: selectedLocation.lat,
        long: selectedLocation.long,
        range,
        sort: sortBy,
        addressTaskFieldId: selectedTaskFieldId,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedTaskFieldId('');
    setSelectedLocation(null);
    setSearchAddress('');
    setRange(10);
    setSortBy('distance');
    onClose();
  };

  const canProceed = currentStep === 1 ? selectedTaskFieldId !== '' : selectedLocation !== null;
  const isStep2Complete = selectedLocation && range > 0;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen={isMobile} maxWidth='md' fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography className={classes.stepIndicator}>Step {currentStep} of 2</Typography>
            <Typography variant='h6'>
              {currentStep === 1 ? 'Select Address Field' : 'Search Location & Filters'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {currentStep === 1 && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography className={classes.description}>
                Choose which address field you want to search by. <br />
                This will search for users who have completed tasks containing the selected address field.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl variant='outlined' fullWidth>
                <InputLabel>Address Task Field</InputLabel>
                <Select
                  value={selectedTaskFieldId}
                  onChange={(e) => setSelectedTaskFieldId(e.target.value as string)}
                  label='Address Task Field'
                >
                  {addressTaskFields.map((field) => (
                    <MenuItem key={field.taskFieldId} value={field.taskFieldId}>
                      <Box>
                        <Typography variant='body1'>{field.name}</Typography>
                        {field.description && (
                          <Typography variant='body2' color='textSecondary'>
                            {field.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}

        {currentStep === 2 && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography className={classes.description}>
                Search for an address and set your distance and sorting preferences. You can drag the pin to adjust the
                exact location.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <AddressSearchField
                label='Search Address'
                placeholder='Enter an address to search...'
                value={searchAddress}
                onChange={setSearchAddress}
                onLocationSelect={handleLocationSelect}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {selectedLocation && (
              <Grid item xs={12}>
                <Box className={classes.mapContainer}>
                  <LocationSearchMap
                    lat={selectedLocation.lat}
                    long={selectedLocation.long}
                    onLocationChange={handleMarkerDrag}
                    draggable
                    showCircle
                    circleRadius={range}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={6}>
              <Box className={classes.filterBox}>
                <Typography variant='subtitle2' gutterBottom>
                  Distance Range
                </Typography>
                <TextField
                  fullWidth
                  type='number'
                  value={range}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === '') {
                      setRange(0);
                    } else {
                      const numValue = Number(value);
                      if (numValue >= 0 && numValue <= 1000) {
                        setRange(numValue);
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position='end'>km</InputAdornment>,
                    inputProps: { min: 0, max: 1000 },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box className={classes.filterBox}>
                <Typography variant='subtitle2' gutterBottom>
                  Sort By
                </Typography>
                <FormControl variant='outlined' fullWidth>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'distance' | 'distance_desc' | 'alphabetical')}
                  >
                    <MenuItem value='distance'>Closest to location</MenuItem>
                    <MenuItem value='distance_desc'>Furthest from location</MenuItem>
                    <MenuItem value='alphabetical'>Alphabetical</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {currentStep === 2 && (
          <TabletButton variant='text' onClick={handleBack} style={{ marginRight: 'auto', textTransform: 'none' }}>
            Back
          </TabletButton>
        )}

        <TabletButton
          variant='text'
          color='error'
          onClick={handleClose}
          style={{ marginRight: 8, textTransform: 'none' }}
        >
          Cancel
        </TabletButton>

        {currentStep === 1 && (
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleNext}
            disabled={!canProceed}
            endIcon={<NavigateNext />}
            style={{ textTransform: 'none' }}
          >
            Next
          </TabletButton>
        )}

        {currentStep === 2 && (
          <TabletButton
            variant='contained'
            color='primary'
            onClick={handleSearch}
            disabled={!isStep2Complete}
            startIcon={<LocationOn />}
            style={{ textTransform: 'none' }}
          >
            Search
          </TabletButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
